import React, {useEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {useLocation, useNavigate} from "react-router-dom";
import {useSelector} from "react-redux";
import {selectCompanyInfo} from "store/companySlice";
import styled from "styled-components";
import Api from "api/Api";
import MainContainer from "layout/atoms/MainContainer";

const Container = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px 0;

  @media (max-width: 768px) {
    padding: 0 0 80px;
  }
`;

const HeaderSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 40px;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 16px;
    margin-bottom: 24px;
  }
`;

const HeaderContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Header = styled.h1`
  font-size: 28px;
  font-weight: 600;
  color: var(--black);
  margin: 0;

  @media (max-width: 768px) {
    font-size: 22px;
  }
`;

const Description = styled.p`
  font-size: 14px;
  color: var(--gray);
  margin: 0;
`;

const NewJobButton = styled.button`
  background: var(--brand-ideolo);
  color: white;
  border: none;
  border-radius: 6px;
  padding: 10px 20px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #4bc235;
    box-shadow: 0 2px 8px rgba(83, 210, 61, 0.3);
  }

  &:active {
    transform: translateY(1px);
  }

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const JobsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const JobCard = styled.div`
  background: var(--content-box-bg);
  border: 1px solid ${({ status }) =>
    status === "running"
      ? "#1f75cb"
      : status === "success"
      ? "#108548"
      : status === "failed"
      ? "#dd2b0e"
      : "#e1e4e8"};
  border-radius: 8px;
  padding: 16px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition: box-shadow 0.2s ease;
  cursor: pointer;

  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }

  @media (max-width: 768px) {
    flex-wrap: wrap;
    gap: 12px;
    padding: 12px 16px;
  }
`;

const JobLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 1;
  min-width: 0;
`;

const StatusIcon = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ status }) =>
    status === "running"
      ? "#1f75cb"
      : status === "success"
      ? "#108548"
      : status === "failed"
      ? "#dd2b0e"
      : "#6e7681"};
  color: white;
  font-size: 10px;
  font-weight: 600;

  ${({ status }) =>
    status === "running" &&
    `
    animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;

    @keyframes pulse {
      0%, 100% {
        opacity: 1;
      }
      50% {
        opacity: 0.5;
      }
    }
  `}
`;

const JobInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
  min-width: 0;
`;

const JobTitle = styled.div`
  display: flex;
  align-items: baseline;
  gap: 6px;
  font-size: 14px;
  min-width: 0;
  width: fit-content;
  max-width: 100%;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

const JobTitlePrefix = styled.span`
  font-weight: 600;
  color: var(--black);
  flex-shrink: 0;
`;

const JobTitlePreview = styled.span`
  color: var(--gray);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
`;

const JobMeta = styled.div`
  font-size: 12px;
  color: var(--gray);
  display: flex;
  gap: 16px;

  @media (max-width: 768px) {
    flex-wrap: wrap;
    gap: 8px;
  }
`;

const JobRight = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;

  @media (max-width: 768px) {
    width: 100%;
    justify-content: space-between;
    gap: 12px;
  }
`;

const ProgressSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 6px;

  @media (max-width: 768px) {
    align-items: flex-start;
    flex: 1;
  }
`;

const ProgressBar = styled.div`
  width: 200px;
  height: 8px;
  background: #e1e4e8;
  border-radius: 4px;
  overflow: hidden;
  position: relative;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const ProgressFill = styled.div`
  height: 100%;
  background: ${({ status }) =>
    status === "running"
      ? "#1f75cb"
      : status === "success"
      ? "#108548"
      : status === "failed"
      ? "#dd2b0e"
      : "#6e7681"};
  border-radius: 4px;
  width: ${({ progress }) => progress}%;
  transition: width 0.3s ease;
`;

const TaskStats = styled.div`
  display: flex;
  gap: 12px;
  font-size: 12px;
`;

const TaskStat = styled.span`
  color: ${({ type }) =>
    type === "error" ? "#dd2b0e" : type === "success" ? "#108548" : "#6e7681"};
  font-weight: 500;
`;

const Duration = styled.div`
  font-size: 12px;
  color: var(--gray);
  min-width: 60px;
  text-align: right;
`;

const JobActions = styled.div`
  position: relative;
`;

const MenuButton = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 6px;
  border: 1px solid #d0d7de;
  background: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  color: var(--gray);
  transition: all 0.2s ease;

  &:hover {
    background: #f6f8fa;
    border-color: #8c959f;
  }

  &:active {
    transform: scale(0.95);
  }
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 4px;
  background: white;
  border: 1px solid #d0d7de;
  border-radius: 6px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  min-width: 160px;
  z-index: 100;
  overflow: hidden;
  display: ${({ isOpen }) => (isOpen ? "block" : "none")};
`;

const DropdownItem = styled.button`
  width: 100%;
  padding: 10px 16px;
  border: none;
  background: white;
  text-align: left;
  font-size: 14px;
  color: ${({ danger }) => (danger ? "#dd2b0e" : "var(--black)")};
  cursor: pointer;
  transition: background 0.2s ease;
  display: flex;
  align-items: center;
  gap: 8px;

  &:hover {
    background: ${({ danger }) => (danger ? "#fff1f0" : "#f6f8fa")};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const AgentsSection = styled.div`
  margin-top: 60px;

  @media (max-width: 768px) {
    margin-top: 40px;
  }
`;

const SectionHeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;

  @media (max-width: 768px) {
    flex-wrap: wrap;
    gap: 12px;
  }
`;

const SectionHeader = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: var(--black);
  margin: 0;
`;

const PairAgentButton = styled.button`
  background: white;
  color: var(--brand-ideolo);
  border: 2px solid var(--brand-ideolo);
  border-radius: 6px;
  padding: 8px 16px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: var(--brand-ideolo);
    color: white;
  }

  &:active {
    transform: translateY(1px);
  }
`;

const AgentCard = styled.div`
  background: var(--content-box-bg);
  border: 1px solid ${({ status }) =>
    status === "busy"
      ? "#1f75cb"
      : status === "idle"
      ? "#108548"
      : "#d0d7de"};
  border-radius: 8px;
  padding: 16px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition: box-shadow 0.2s ease;

  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }

  @media (max-width: 768px) {
    flex-wrap: wrap;
    gap: 12px;
    padding: 12px 16px;
  }
`;

const AgentLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 1;

  @media (max-width: 768px) {
    min-width: 0;
  }
`;

const AgentStatusDot = styled.div`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: ${({ status }) =>
    status === "busy"
      ? "#1f75cb"
      : status === "idle"
      ? "#108548"
      : "#6e7681"};

  ${({ status }) =>
    status === "busy" &&
    `
    animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
  `}
`;

const AgentInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const AgentName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: var(--black);
`;

const AgentMeta = styled.div`
  font-size: 12px;
  color: var(--gray);
  display: flex;
  gap: 16px;

  @media (max-width: 768px) {
    flex-wrap: wrap;
    gap: 8px;
  }
`;

const AgentRight = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;

  @media (max-width: 768px) {
    width: 100%;
    justify-content: space-between;
    gap: 12px;
  }
`;

const AgentStat = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
`;

const StatLabel = styled.div`
  font-size: 11px;
  color: var(--gray);
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const StatValue = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: var(--black);
`;

const BatteryIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const BatteryBar = styled.div`
  width: 40px;
  height: 16px;
  border: 2px solid #6e7681;
  border-radius: 3px;
  position: relative;
  overflow: hidden;

  &::after {
    content: "";
    position: absolute;
    top: 50%;
    right: -4px;
    transform: translateY(-50%);
    width: 2px;
    height: 8px;
    background: #6e7681;
  }
`;

const BatteryFill = styled.div`
  height: 100%;
  width: ${({ level }) => level}%;
  background: ${({ level }) =>
    level > 50 ? "#108548" : level > 20 ? "#ebbd23" : "#dd2b0e"};
  transition: width 0.3s ease;
`;

const StatusBadge = styled.span`
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  background: ${({ status }) =>
    status === "busy"
      ? "#ddf4ff"
      : status === "idle"
      ? "#dafbe1"
      : "#f6f8fa"};
  color: ${({ status }) =>
    status === "busy"
      ? "#0969da"
      : status === "idle"
      ? "#1a7f37"
      : "#57606a"};
`;

const AgentActions = styled.div`
  position: relative;
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: ${({ isOpen }) => (isOpen ? "flex" : "none")};
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 12px;
  padding: 32px;
  max-width: 600px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);

  @media (max-width: 768px) {
    padding: 20px 16px;
    width: 95%;
    max-height: 90vh;
    border-radius: 8px;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const ModalTitle = styled.h2`
  font-size: 24px;
  font-weight: 600;
  color: var(--black);
  margin: 0;
`;

const CloseButton = styled.button`
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  font-size: 24px;
  color: var(--gray);
  cursor: pointer;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s ease;

  &:hover {
    background: #f6f8fa;
  }
`;

const DetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 12px 0;
  border-bottom: 1px solid #e1e4e8;

  &:last-child {
    border-bottom: none;
  }
`;

const DetailLabel = styled.span`
  font-size: 14px;
  color: var(--gray);
  font-weight: 500;
`;

const DetailValue = styled.span`
  font-size: 14px;
  color: var(--black);
  font-weight: 600;
`;

const MessageBox = styled.div`
  background: #f6f8fa;
  border: 1px solid #d0d7de;
  border-radius: 6px;
  padding: 16px;
  margin: 16px 0;
  font-size: 14px;
  line-height: 1.6;
  color: var(--black);
  white-space: pre-wrap;
  font-family: -apple-system, system-ui, BlinkMacSystemFont, "Segoe UI", Roboto;
`;

const TagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
`;

const Tag = styled.span`
  background: #ddf4ff;
  color: #0969da;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
`;

const LoadingText = styled.div`
  text-align: center;
  padding: 32px;
  color: var(--gray);
  font-size: 14px;
`;

const QrCodeContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  padding: 20px 0;
`;

const QrCodeImage = styled.img`
  width: 300px;
  height: 300px;
  border: 2px solid #e1e4e8;
  border-radius: 8px;

  @media (max-width: 768px) {
    width: 220px;
    height: 220px;
  }
`;

const QrCodeInstructions = styled.div`
  text-align: center;
  color: var(--gray);
  font-size: 14px;
  line-height: 1.6;
  max-width: 400px;
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  margin-top: 24px;
  padding: 16px 0;

  @media (max-width: 768px) {
    flex-wrap: wrap;
    gap: 6px;
  }
`;

const PageButton = styled.button`
  min-width: 36px;
  height: 36px;
  padding: 0 12px;
  border: 1px solid ${({ active }) => (active ? "var(--brand-ideolo)" : "#d0d7de")};
  background: ${({ active }) => (active ? "var(--brand-ideolo)" : "white")};
  color: ${({ active }) => (active ? "white" : "var(--black)")};
  border-radius: 6px;
  font-size: 14px;
  font-weight: ${({ active }) => (active ? "600" : "500")};
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
  opacity: ${({ disabled }) => (disabled ? "0.5" : "1")};
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: ${({ active }) => (active ? "#4bc235" : "#f6f8fa")};
    border-color: ${({ active }) => (active ? "#4bc235" : "#8c959f")};
  }

  &:active:not(:disabled) {
    transform: scale(0.95);
  }
`;

const PageInfo = styled.span`
  font-size: 14px;
  color: var(--gray);
  margin: 0 8px;
`;

// Mock data removed - now fetched from API endpoints:
// - Jobs: /api/v1/msg/sms/jobs
// - Agents: /api/v1/msg/sms/agents

function SmsSending() {
  const navigate = useNavigate();
  const location = useLocation();
  const companyInfo = useSelector(selectCompanyInfo);
  const { t } = useTranslation("translation", {
    keyPrefix: "messaging.smsSending",
  });
  const [companyId, setCompanyId] = useState(companyInfo.id);
  const [agents, setAgents] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [loading, setLoading] = useState(true);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [openAgentMenuId, setOpenAgentMenuId] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);
  const [jobDetails, setJobDetails] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [showPairingModal, setShowPairingModal] = useState(false);
  const [qrCodeData, setQrCodeData] = useState(null);
  const [loadingQrCode, setLoadingQrCode] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const api = new Api();
  const menuRef = useRef(null);
  const agentMenuRef = useRef(null);
  const lastJobsRefreshTime = useRef(Date.now());
  const lastAgentsRefreshTime = useRef(Date.now());
  const jobsRef = useRef(jobs);
  const agentsRef = useRef(agents);
  const loadingRef = useRef(loading);
  const companyIdRef = useRef(companyId);
  const currentPageRef = useRef(currentPage);

  // Keep refs in sync with state
  useEffect(() => {
    jobsRef.current = jobs;
    agentsRef.current = agents;
    loadingRef.current = loading;
    companyIdRef.current = companyId;
    currentPageRef.current = currentPage;
  }, [jobs, agents, loading, companyId, currentPage]);

  // Helper function to map job DTO to component format
  const mapJobDto = (job) => {
    const progress = job.tasks.total > 0
      ? Math.round((job.tasks.done / job.tasks.total) * 100)
      : 0;

    let status = 'pending';
    if (job.status === 'running') status = 'running';
    else if (job.status === 'done') status = 'success';
    else if (job.status === 'stale') status = 'failed';
    else if (job.status === 'awaiting') status = 'pending';

    return {
      id: job.id,
      status: status,
      progress: progress,
      completedTasks: job.tasks.done,
      errorTasks: job.tasks.error,
      totalTasks: job.tasks.total,
      createdAt: new Date(job.createdAt),
      startedAt: job.startedAt ? new Date(job.startedAt) : null,
      finishedAt: job.finishedAt ? new Date(job.finishedAt) : null,
      message: job.message,
    };
  };

  useEffect(() => {
    const fetchData = async () => {
      // Company info is loaded asynchronously; wait until it's available
      const currentCompanyId = companyInfo.id;
      if (!currentCompanyId) {
        return;
      }

      try {
        setCompanyId(currentCompanyId);

        // Fetch agents
        const agentsData = await api.getSmsAgents(currentCompanyId);
        const mappedAgents = agentsData.map((agent) => ({
          id: agent.id,
          name: agent.name,
          lastSeenAt: new Date(agent.lastSeenAt),
          status: agent.status,
          appVersion: agent.version,
          battery: agent.battery,
          assignedSmsJobId: null,
        }));
        setAgents(mappedAgents);

        // Fetch jobs with pagination
        const jobsResponse = await api.getSmsJobs(currentCompanyId, currentPage);
        const mappedJobs = jobsResponse.content.map(mapJobDto);
        setJobs(mappedJobs);
        setTotalPages(jobsResponse.totalPages);
        setTotalElements(jobsResponse.totalElements);
      } catch (error) {
        console.error("Failed to fetch data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, companyInfo.id]);

  // Handle new job from navigation state
  useEffect(() => {
    if (location.state?.newJob) {
      const mappedNewJob = mapJobDto(location.state.newJob);
      setJobs(prevJobs => [mappedNewJob, ...prevJobs]);

      // Clear the navigation state to prevent re-adding on refresh
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state]);

  // Update current time every second for real-time duration display
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  // Silent refresh of jobs and agents lists with dynamic intervals
  useEffect(() => {
    const checkAndRefresh = async () => {
      const now = Date.now();
      const currentJobs = jobsRef.current;
      const currentAgents = agentsRef.current;
      const currentLoading = loadingRef.current;

      // Jobs refresh logic
      const hasRunningJob = currentJobs.some(job => job.status === 'running');
      const isJobsEmpty = currentJobs.length === 0 && !currentLoading;

      let shouldRefreshJobs = false;
      let requiredJobsInterval = 0;

      // Check if running job: refresh every 5s
      if (hasRunningJob) {
        requiredJobsInterval = 5000;
      }
      // Check if empty: refresh every 20s
      else if (isJobsEmpty) {
        requiredJobsInterval = 20000;
      }
      // Otherwise: refresh every 30s to catch status changes
      else {
        requiredJobsInterval = 30000;
      }

      if (requiredJobsInterval > 0 && now - lastJobsRefreshTime.current >= requiredJobsInterval) {
        shouldRefreshJobs = true;
      }

      if (shouldRefreshJobs && companyIdRef.current) {
        lastJobsRefreshTime.current = now;
        try {
          const jobsResponse = await api.getSmsJobs(companyIdRef.current, currentPageRef.current);
          const mappedJobs = jobsResponse.content.map(mapJobDto);
          setJobs(mappedJobs);
          setTotalPages(jobsResponse.totalPages);
          setTotalElements(jobsResponse.totalElements);
        } catch (error) {
          console.error("Failed to refresh jobs:", error);
        }
      }

      // Agents refresh logic
      const hasBusyAgent = currentAgents.some(agent => agent.status === 'busy');
      const isAgentsEmpty = currentAgents.length === 0 && !currentLoading;

      let shouldRefreshAgents = false;
      let requiredAgentsInterval = 0;

      // Check if busy agent: refresh every 5s
      if (hasBusyAgent) {
        requiredAgentsInterval = 5000;
      }
      // Check if empty: refresh every 20s
      else if (isAgentsEmpty) {
        requiredAgentsInterval = 20000;
      }
      // Otherwise: refresh every 30s to keep "last seen" updated
      else {
        requiredAgentsInterval = 30000;
      }

      if (requiredAgentsInterval > 0 && now - lastAgentsRefreshTime.current >= requiredAgentsInterval) {
        shouldRefreshAgents = true;
      }

      if (shouldRefreshAgents && companyIdRef.current) {
        lastAgentsRefreshTime.current = now;
        try {
          const agentsData = await api.getSmsAgents(companyIdRef.current);
          const mappedAgents = agentsData.map((agent) => ({
            id: agent.id,
            name: agent.name,
            lastSeenAt: new Date(agent.lastSeenAt),
            status: agent.status,
            appVersion: agent.version,
            battery: agent.battery,
            assignedSmsJobId: null,
          }));
          setAgents(mappedAgents);
        } catch (error) {
          console.error("Failed to refresh agents:", error);
        }
      }
    };

    // Check every second, but only refresh based on conditions
    const interval = setInterval(checkAndRefresh, 1000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setOpenMenuId(null);
      }
      if (agentMenuRef.current && !agentMenuRef.current.contains(event.target)) {
        setOpenAgentMenuId(null);
      }
    };

    if (openMenuId || openAgentMenuId) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [openMenuId, openAgentMenuId]);

  const toggleMenu = (jobId, e) => {
    e.stopPropagation();
    setOpenMenuId(openMenuId === jobId ? null : jobId);
  };

  const toggleAgentMenu = (agentId, e) => {
    e.stopPropagation();
    setOpenAgentMenuId(openAgentMenuId === agentId ? null : agentId);
  };

  const handleViewDetails = async (job) => {
    setSelectedJob(job);
    setShowDetailsModal(true);
    setOpenMenuId(null);
    setLoadingDetails(true);
    setJobDetails(null);

    try {
      const details = await api.getSmsJobDetails(companyId, job.id);
      setJobDetails(details);
    } catch (error) {
      console.error("Failed to fetch job details:", error);
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleCancelJob = async (job) => {
    if (!window.confirm(`${t("jobCard.cancelConfirm")} #${job.id}?`)) {
      return;
    }

    try {
      await api.cancelSmsJob(companyId, job.id);

      // Remove from list after successful cancellation
      setJobs(prevJobs => prevJobs.filter(j => j.id !== job.id));
      setOpenMenuId(null);
    } catch (error) {
      console.error("Failed to cancel job:", error);
      alert("Failed to cancel job. Please try again.");
    }
  };

  const handlePairAgent = async () => {
    setShowPairingModal(true);
    setLoadingQrCode(true);
    setQrCodeData(null);

    try {
      const data = await api.getAgentAuthQrCode();
      setQrCodeData(data);
    } catch (error) {
      console.error("Failed to fetch QR code:", error);
      alert("Failed to generate QR code. Please try again.");
      setShowPairingModal(false);
    } finally {
      setLoadingQrCode(false);
    }
  };

  const handleForgetAgent = async (agent) => {
    setOpenAgentMenuId(null);

    if (!window.confirm(t("agentCard.forgetConfirm", { name: agent.name }))) {
      return;
    }

    try {
      await api.forgetSmsAgent(companyId, agent.id);

      // Remove from list after successful forget
      setAgents(prevAgents => prevAgents.filter(a => a.id !== agent.id));
    } catch (error) {
      console.error("Failed to forget agent:", error);
      alert("Failed to forget agent. Please try again.");
    }
  };

  const formatDuration = (job) => {
    if (!job.startedAt) return t("jobCard.notStarted");

    const end = job.finishedAt || currentTime;
    const duration = Math.floor((end - job.startedAt) / 1000);

    const minutes = Math.floor(duration / 60);
    const seconds = duration % 60;

    if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    }
    return `${seconds}s`;
  };

  const formatTimestamp = (date) => {
    const diff = Math.floor((currentTime - date) / 1000);

    if (diff < 60) return `${diff} ${t("timeAgo.secondsAgo")}`;
    if (diff < 3600) return `${Math.floor(diff / 60)} ${t("timeAgo.minutesAgo")}`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} ${t("timeAgo.hoursAgo")}`;
    return `${Math.floor(diff / 86400)} ${t("timeAgo.daysAgo")}`;
  };

  const getStatusIcon = (status) => {
    if (status === "running") return "▶";
    if (status === "success") return "✓";
    if (status === "failed") return "✕";
    return "○";
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 0 && newPage < totalPages) {
      setCurrentPage(newPage);
    }
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(0, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages - 1, startPage + maxVisiblePages - 1);

    // Adjust start if we're near the end
    if (endPage - startPage < maxVisiblePages - 1) {
      startPage = Math.max(0, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <PageButton
          key={i}
          active={i === currentPage}
          onClick={() => handlePageChange(i)}
        >
          {i + 1}
        </PageButton>
      );
    }

    return (
      <PaginationContainer>
        <PageButton
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 0}
        >
          ←
        </PageButton>
        {startPage > 0 && (
          <>
            <PageButton onClick={() => handlePageChange(0)}>1</PageButton>
            {startPage > 1 && <PageInfo>...</PageInfo>}
          </>
        )}
        {pages}
        {endPage < totalPages - 1 && (
          <>
            {endPage < totalPages - 2 && <PageInfo>...</PageInfo>}
            <PageButton onClick={() => handlePageChange(totalPages - 1)}>
              {totalPages}
            </PageButton>
          </>
        )}
        <PageButton
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages - 1}
        >
          →
        </PageButton>
        <PageInfo>
          Page {currentPage + 1} of {totalPages} ({totalElements} total)
        </PageInfo>
      </PaginationContainer>
    );
  };

  return (
    <MainContainer>
    <Container>
      <HeaderSection>
        <HeaderContent>
          <Header>{t("header")}</Header>
          <Description>{t("description")}</Description>
        </HeaderContent>
        <NewJobButton onClick={() => navigate("/admin/messaging/sms/new")}>
          {t("newJobButton")}
        </NewJobButton>
      </HeaderSection>

      {loading ? (
        <div>{t("loadingJobs")}</div>
      ) : jobs.length === 0 ? (
        <div>{t("noJobs")}</div>
      ) : (
        <JobsList>
          {jobs.map((job) => (
          <JobCard key={job.id} status={job.status}>
            <JobLeft>
              <StatusIcon status={job.status}>
                {getStatusIcon(job.status)}
              </StatusIcon>
              <JobInfo>
                <JobTitle onClick={() => handleViewDetails(job)}>
                  <JobTitlePrefix>{t("jobCard.smsLabel")}</JobTitlePrefix>
                  <JobTitlePreview>{job.message || t("jobCard.noMessage")}</JobTitlePreview>
                </JobTitle>
                <JobMeta>
                  <span>{t("jobCard.created")} {formatTimestamp(job.createdAt)}</span>
                  {job.status === "running" && <span>{t("jobCard.running")}</span>}
                  {job.status === "success" && <span>{t("jobCard.completed")}</span>}
                  {job.status === "failed" && <span>{t("jobCard.failed")}</span>}
                </JobMeta>
              </JobInfo>
            </JobLeft>

            <JobRight>
              <ProgressSection>
                <ProgressBar>
                  <ProgressFill progress={job.progress} status={job.status} />
                </ProgressBar>
                <TaskStats>
                  <TaskStat type="success">{job.completedTasks} {t("jobCard.sent")}</TaskStat>
                  {job.errorTasks > 0 && (
                    <TaskStat type="error">{job.errorTasks} {t("jobCard.failed")}</TaskStat>
                  )}
                  <TaskStat>{t("jobCard.of")} {job.totalTasks}</TaskStat>
                </TaskStats>
              </ProgressSection>

              <Duration>{formatDuration(job)}</Duration>

              <JobActions ref={openMenuId === job.id ? menuRef : null}>
                <MenuButton onClick={(e) => toggleMenu(job.id, e)}>
                  ⋮
                </MenuButton>
                <DropdownMenu isOpen={openMenuId === job.id}>
                  <DropdownItem
                    onClick={() => handleCancelJob(job)}
                    danger
                    disabled={job.status === "success"}
                  >
                    ✕ {t("jobCard.cancelJob")}
                  </DropdownItem>
                </DropdownMenu>
              </JobActions>
            </JobRight>
          </JobCard>
          ))}
        </JobsList>
      )}

      {!loading && renderPagination()}

      <AgentsSection>
        <SectionHeaderRow>
          <SectionHeader>{t("smsAgentsHeader")}</SectionHeader>
          <PairAgentButton onClick={handlePairAgent}>
            {t("pairAgentButton")}
          </PairAgentButton>
        </SectionHeaderRow>
        {loading ? (
          <div>{t("loadingAgents")}</div>
        ) : agents.length === 0 ? (
          <div>{t("noAgents")}</div>
        ) : (
          <JobsList>
            {agents.map((agent) => (
            <AgentCard key={agent.id} status={agent.status}>
              <AgentLeft>
                <AgentStatusDot status={agent.status} />
                <AgentInfo>
                  <AgentName>{agent.name}</AgentName>
                  <AgentMeta>
                    <span>{t("agentCard.version")}{agent.appVersion}</span>
                    <span>•</span>
                    <span>{t("agentCard.lastSeen")} {formatTimestamp(agent.lastSeenAt)}</span>
                    {agent.assignedSmsJobId && (
                      <>
                        <span>•</span>
                        <span>
                          {t("agentCard.workingOn")} #
                          {agent.assignedSmsJobId.split("-")[1]}
                        </span>
                      </>
                    )}
                  </AgentMeta>
                </AgentInfo>
              </AgentLeft>

              <AgentRight>
                <AgentStat>
                  <StatLabel>{t("agentCard.status")}</StatLabel>
                  <StatusBadge status={agent.status}>
                    {t(`agentCard.${agent.status}`)}
                  </StatusBadge>
                </AgentStat>

                <AgentStat>
                  <StatLabel>{t("agentCard.battery")}</StatLabel>
                  <BatteryIndicator>
                    <BatteryBar>
                      <BatteryFill level={agent.battery} />
                    </BatteryBar>
                    <StatValue>{agent.battery}%</StatValue>
                  </BatteryIndicator>
                </AgentStat>

                <AgentActions ref={openAgentMenuId === agent.id ? agentMenuRef : null}>
                  <MenuButton onClick={(e) => toggleAgentMenu(agent.id, e)}>
                    ⋮
                  </MenuButton>
                  <DropdownMenu isOpen={openAgentMenuId === agent.id}>
                    <DropdownItem
                      onClick={() => handleForgetAgent(agent)}
                      danger
                    >
                      🗑️ {t("agentCard.forgetButton")}
                    </DropdownItem>
                  </DropdownMenu>
                </AgentActions>
              </AgentRight>
            </AgentCard>
            ))}
          </JobsList>
        )}
      </AgentsSection>

      {/* Pair Agent Modal */}
      <ModalOverlay
        isOpen={showPairingModal}
        onClick={() => setShowPairingModal(false)}
      >
        <ModalContent onClick={(e) => e.stopPropagation()}>
          <ModalHeader>
            <ModalTitle>{t("pairModal.title")}</ModalTitle>
            <CloseButton onClick={() => setShowPairingModal(false)}>
              ×
            </CloseButton>
          </ModalHeader>

          {loadingQrCode ? (
            <LoadingText>{t("pairModal.loading")}</LoadingText>
          ) : qrCodeData ? (
            <QrCodeContainer>
              <QrCodeImage
                src={`data:image/png;base64,${qrCodeData.qrCodeImageBase64}`}
                alt="QR Code for agent pairing"
              />
              <QrCodeInstructions>
                {t("pairModal.instructions")}
              </QrCodeInstructions>
            </QrCodeContainer>
          ) : null}
        </ModalContent>
      </ModalOverlay>

      {/* Job Details Modal */}
      <ModalOverlay
        isOpen={showDetailsModal}
        onClick={() => setShowDetailsModal(false)}
      >
        <ModalContent onClick={(e) => e.stopPropagation()}>
          <ModalHeader>
            <ModalTitle>{t("detailsModal.title")}</ModalTitle>
            <CloseButton onClick={() => setShowDetailsModal(false)}>
              ×
            </CloseButton>
          </ModalHeader>

          {loadingDetails ? (
            <LoadingText>{t("detailsModal.loading")}</LoadingText>
          ) : selectedJob && jobDetails ? (
            <div>
              <DetailRow>
                <DetailLabel>{t("detailsModal.jobId")}</DetailLabel>
                <DetailValue>{selectedJob.id}</DetailValue>
              </DetailRow>
              <DetailRow>
                <DetailLabel>{t("detailsModal.status")}</DetailLabel>
                <DetailValue style={{ textTransform: 'capitalize' }}>
                  {selectedJob.status}
                </DetailValue>
              </DetailRow>
              <DetailRow>
                <DetailLabel>{t("detailsModal.progress")}</DetailLabel>
                <DetailValue>{selectedJob.progress}%</DetailValue>
              </DetailRow>
              <DetailRow>
                <DetailLabel>{t("detailsModal.completedTasks")}</DetailLabel>
                <DetailValue>{selectedJob.completedTasks}</DetailValue>
              </DetailRow>
              <DetailRow>
                <DetailLabel>{t("detailsModal.failedTasks")}</DetailLabel>
                <DetailValue>{selectedJob.errorTasks}</DetailValue>
              </DetailRow>
              <DetailRow>
                <DetailLabel>{t("detailsModal.totalTasks")}</DetailLabel>
                <DetailValue>{selectedJob.totalTasks}</DetailValue>
              </DetailRow>
              <DetailRow>
                <DetailLabel>{t("detailsModal.createdAt")}</DetailLabel>
                <DetailValue>
                  {selectedJob.createdAt.toLocaleString()}
                </DetailValue>
              </DetailRow>
              <DetailRow>
                <DetailLabel>{t("detailsModal.startedAt")}</DetailLabel>
                <DetailValue>
                  {selectedJob.startedAt
                    ? selectedJob.startedAt.toLocaleString()
                    : t("jobCard.notStarted")}
                </DetailValue>
              </DetailRow>
              <DetailRow>
                <DetailLabel>{t("detailsModal.finishedAt")}</DetailLabel>
                <DetailValue>
                  {selectedJob.finishedAt
                    ? selectedJob.finishedAt.toLocaleString()
                    : t("detailsModal.notFinished")}
                </DetailValue>
              </DetailRow>
              <DetailRow>
                <DetailLabel>{t("detailsModal.duration")}</DetailLabel>
                <DetailValue>{formatDuration(selectedJob)}</DetailValue>
              </DetailRow>

              {jobDetails.audience?.byTags && jobDetails.audience.byTags.length > 0 && (
                <DetailRow>
                  <DetailLabel>{t("detailsModal.audienceTags")}</DetailLabel>
                  <TagsContainer>
                    {jobDetails.audience.byTags.map((tag, index) => (
                      <Tag key={index}>{tag}</Tag>
                    ))}
                  </TagsContainer>
                </DetailRow>
              )}

              {jobDetails.message && (
                <>
                  <DetailRow>
                    <DetailLabel>{t("detailsModal.message")}</DetailLabel>
                  </DetailRow>
                  <MessageBox>{jobDetails.message}</MessageBox>
                </>
              )}
            </div>
          ) : null}
        </ModalContent>
      </ModalOverlay>
    </Container>
    </MainContainer>
  );
}

export default SmsSending;
